import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { eventAPI } from '../../../services/eventApi';
import { EventLinkVisibility, EventStatus, EventWriteInput } from '../../../types/event';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton';
import ErrorState from '../../../components/ui/ErrorState';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const selectClass =
  'w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0';

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromLocalInput(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

function defaultStart(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return toLocalInput(date.toISOString());
}

export default function EventEditor() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<EventStatus>('DRAFT');
  const [startAt, setStartAt] = useState(defaultStart);
  const [endAt, setEndAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [timezone, setTimezone] = useState('America/Toronto');
  const [venue, setVenue] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [meetingVisibility, setMeetingVisibility] = useState<EventLinkVisibility>('AFTER_REGISTRATION');
  const [evaluationUrl, setEvaluationUrl] = useState('');
  const [evaluationVisibility, setEvaluationVisibility] = useState<EventLinkVisibility>('AFTER_REGISTRATION');

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    eventAPI
      .fetchStaffEvent(id)
      .then((event) => {
        if (cancelled) return;
        setTitle(event.title);
        setSlug(event.slug);
        setDescription(event.description);
        setStatus(event.status);
        setStartAt(toLocalInput(event.startAt));
        setEndAt(toLocalInput(event.endAt));
        setAllDay(event.allDay);
        setTimezone(event.timezone);
        setVenue(event.venue ?? '');
        setWebsiteUrl(event.websiteUrl ?? '');
        setRecordingUrl(event.recordingUrl ?? '');
        setMeetingUrl(event.meetingUrl ?? '');
        setMeetingVisibility(event.meetingVisibility);
        setEvaluationUrl(event.evaluationUrl ?? '');
        setEvaluationVisibility(event.evaluationVisibility);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Failed to load event'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const start = fromLocalInput(startAt);
    if (!start) {
      setError(new Error('A start date is required'));
      return;
    }
    const input: EventWriteInput = {
      title: title.trim(),
      slug: slug.trim(),
      description,
      status,
      startAt: start,
      endAt: fromLocalInput(endAt),
      allDay,
      timezone: timezone.trim() || 'America/Toronto',
      venue: venue.trim() || null,
      websiteUrl: websiteUrl.trim() || null,
      recordingUrl: recordingUrl.trim() || null,
      meetingUrl: meetingUrl.trim() || null,
      meetingVisibility,
      evaluationUrl: evaluationUrl.trim() || null,
      evaluationVisibility,
    };
    setSaving(true);
    setError(null);
    try {
      const saved = isNew ? await eventAPI.createEvent(input) : await eventAPI.updateEvent(id, input);
      if (isNew) navigate(`/admin/events/${saved.id}/edit`);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save event'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSkeleton lines={8} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb
        items={[
          { label: 'Home', to: '/' },
          { label: 'Events', to: '/admin/events' },
          { label: isNew ? 'New event' : title || 'Edit event' },
        ]}
      />
      <Link to="/admin/events" className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-6">
        <ArrowLeftIcon className="h-5 w-5" />
        Back to events
      </Link>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{isNew ? 'New event' : 'Edit event'}</h1>
      {error && <div className="mb-4"><ErrorState error={error} title="Could not save the event" /></div>}
      <form onSubmit={save} className="space-y-4">
        <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Input
          label="Slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          onBlur={() => {
            if (!slug.trim() && title.trim()) setSlug(slugify(title));
          }}
          required
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="event-description">Description</label>
          <textarea
            id="event-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={8}
            className={selectClass}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="event-status">Status</label>
          <select id="event-status" className={selectClass} value={status} onChange={(e) => setStatus(e.target.value as EventStatus)}>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
          </select>
        </div>
        <Input label="Starts" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} required />
        <Input label="Ends" type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="h-5 w-5 accent-primary" />
          All day
        </label>
        <Input label="Time zone" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
        <Input label="Venue" value={venue} onChange={(e) => setVenue(e.target.value)} />
        <Input label="Website or registration URL" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://" />
        <Input label="Recording URL" value={recordingUrl} onChange={(e) => setRecordingUrl(e.target.value)} placeholder="https://youtube.com/..." />
        <Input label="Meeting link" value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)} placeholder="https://" />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="meeting-visibility">Meeting link visibility</label>
          <select id="meeting-visibility" className={selectClass} value={meetingVisibility} onChange={(e) => setMeetingVisibility(e.target.value as EventLinkVisibility)}>
            <option value="PUBLIC">Public</option>
            <option value="AFTER_REGISTRATION">Only after registration</option>
          </select>
        </div>
        <Input label="Evaluation link" value={evaluationUrl} onChange={(e) => setEvaluationUrl(e.target.value)} placeholder="https://" />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="evaluation-visibility">Evaluation link visibility</label>
          <select id="evaluation-visibility" className={selectClass} value={evaluationVisibility} onChange={(e) => setEvaluationVisibility(e.target.value as EventLinkVisibility)}>
            <option value="PUBLIC">Public</option>
            <option value="AFTER_REGISTRATION">Only after registration</option>
          </select>
        </div>
        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save event'}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/events')}>Cancel</Button>
        </div>
      </form>
    </div>
  );
}
